import axios from 'axios';
import Api from './Api';

import store from '@/store/store';

const API_URL = process.env.VUE_APP_API_URL;

/**
 * Construit les en-têtes pour les requêtes JSON.
 * @returns {object} Les en-têtes avec les clés d'API et le token d'authentification.
 */
function buildJsonHeaders() {
  const headers = {
    'Content-Type': 'application/json',
    'apikey': process.env.VUE_APP_RTEK8DSR,
    'paraphrase': process.env.VUE_APP_BJ7DC3S,
  };

  if (store.state && store.state.token) {
    headers['Authorization'] = `Bearer ${store.state.token}`;
  }

  return headers;
}

const TiaKTiaKService = {
  /**
   * Crée un devis pour le parcours simplifié.
   * @param {object} devisData - Les données du devis.
   * @returns {Promise<object>} La réponse de l'API.
   */
  async createDevis(devisData) {
    const url = `${API_URL}tiaktiak/devis/add`;
    const body = {
      client: {
        nom: devisData.client.nom,
        prenom: devisData.client.prenom,
        telephone: devisData.client.telephone
      },
      beneficiaire : {
        nom: devisData.beneficiaire.nom,
        prenom: devisData.beneficiaire.prenom || '-'
      },
      ocr_data: {
        immatriculation: devisData.ocr_data.immatriculation,
        marque: devisData.ocr_data.marque,
        modele: devisData.ocr_data.modele,
        puissance_fiscale: parseInt(devisData.ocr_data.puissanceFiscale, 10),
        nombre_places: parseInt(devisData.ocr_data.nombrePlaces, 10),
        type_carburant: devisData.ocr_data.carburant,
        date_premiere_mise_en_circulation: devisData.ocr_data.dateCirculation,
        volumetrie2_roues: parseInt(devisData.ocr_data.volumetrie2_roues, 10)
      },
      duree: devisData.duree,
      dateEffet: devisData.dateEffet || devisData.date_effet,
      assureurCode: 'ASS',
      codeAgent: devisData.codeAgent || null,
      plateforme: 'Front-Aywa'
    };
    if (devisData.beneficiaire && (devisData.beneficiaire.nom || devisData.beneficiaire.prenom)) {
      body.beneficiaire = {
        nom: devisData.beneficiaire.nom,
        prenom: devisData.beneficiaire.prenom
      };
    }
    try {
      const response = await axios.post(url, body, { headers: buildJsonHeaders() });
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la création du devis via TiaKTiaKService:", error.response || error);
      throw error;
    }
  },

  /**
   * Valide un codeAgent et retourne les infos de l'agent associe (pour affichage du badge).
   * Validation informationnelle : si l'endpoint echoue, le codeAgent reste utilisable pour le devis.
   */
  async validateAgent(codeAgent) {
    const url = `${API_URL}tukki/agent/validate?codeAgent=${encodeURIComponent(codeAgent)}`;
    try {
      const response = await axios.get(url, { headers: buildJsonHeaders() });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async getTarification(agentCode) {
    const url = `${API_URL}tiaktiak/tarification`;
    const params = {};
    if (agentCode) {
      params.codeAgent = String(agentCode).trim();
    }
    try {
      const response = await axios.get(url, { headers: buildJsonHeaders(), params });
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la récupération de la tarification via TiaKTiaKService:", error.response || error);
      throw error;
    }
  },

  /**
   * Vérifie si un véhicule est déjà assuré par sa plaque.
   * @param {string} immatriculation - La plaque d'immatriculation.
   * @returns {Promise<object>} Le résultat de la vérification.
   */
  async checkExistingInsurance(immatriculation) {
    const url = `${API_URL}vehicule_management/verify/${immatriculation}`;
    try {
      const response = await axios.get(url, { headers: buildJsonHeaders() });
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la vérification de l'assurance:", error.response || error);
      throw error;
    }
  },

  /**
   * Récupère les détails d'un contrat.
   * @param {string|object} params - Le numéro de devis ou un objet contenant le numeroDevis.
   * @returns {Promise<object>} Les détails du contrat.
   */
  async getContratDetails(params) {
    const url = `${API_URL}tiaktiak/contrat/details`;
    const body = {};
    if (params) {
      body.numeroDevis = typeof params === 'object' ? params.numeroDevis : params;
    } else {
      throw new Error("Le numéro de devis est requis.");
    }

    try {
      const response = await axios.post(url, body, { headers: buildJsonHeaders() });
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la récupération des détails du contrat:", error.response || error);
      throw error;
    }
  },

  async initiateWavePayment(data) {
    const url = `wave/create/session/`;
    const origin = 'https://assuraf.com';
    const payload = {
      amount: data.montant,
      currency: "XOF",
      client_reference: "ASS381" + "_" + data.numeroDevis.replace(/\//g, '_'),
      error_url: `${origin}/payment/aywa/failed`,
      success_url: `${origin}/payment/aywa/wave/?mdoi=${data.numeroDevis.replace(/\//g, '_')}`,
      codeAgent: data.codeAgent || null
    };

    try {
      const response = await Api().post(url, payload);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de l'initialisation du paiement Wave:", error.response || error);
      throw error;
    }
  },

  async payWithOrangeMoneyTiaTchak(data) {
    const url = `orangemoney/onestep`;
    const payload = {
      primettc: data.montant,
      telephone_client: data.telephone,
      code_otp: data.otp,
      reference: data.numeroDevis,
      codeAgent: data.codeAgent || null
    };
    try {
      const response = await Api().post(url, payload);
      return response.data;
    } catch (error) {
      console.error("Erreur lors du paiement Orange Money (Tia Tchak):", error.response || error);
      throw error;
    }
  }
};

export default TiaKTiaKService;
//69e9f702d8a0bb168b59d7d9 id association
